import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import WorkOrders from './pages/WorkOrders';
import WorkOrderDetail from './pages/WorkOrderDetail';
import MaintenancePlan from './pages/MaintenancePlan';
import WorkPlans from './pages/WorkPlans';
import Inventory from './pages/Inventory';
import Purchasing from './pages/Purchasing';
import Reports from './pages/Reports';
import Miscellaneous from './pages/Miscellaneous';
import Layout from './components/Layout';

// ── Auth loading screen ────────────────────────────────────────────────────────
function AuthLoader() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'white', flexDirection: 'column', gap: 16,
    }}>
      <img src={process.env.PUBLIC_URL + '/logo.png'} alt="iSHIP+" style={{ height: 52, objectFit: 'contain' }} />
      <div style={{
        width: 32, height: 32, border: '3px solid rgba(59,96,130,0.2)',
        borderTopColor: '#3B6082', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Block unauthenticated users ───────────────────────────────────────────────
function BlockUnauthenticated({ children }) {
  const { user, loading, authChecked } = useAuth();
  if (!authChecked || loading) return <AuthLoader />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// ── Block users without page permission ───────────────────────────────────────
function RequirePage({ pageKey, children }) {
  const { canAccessPage, user } = useAuth();
  if (!canAccessPage(pageKey)) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', flexDirection: 'column', gap: 12, textAlign: 'center', padding: 20,
      }}>
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <h2 style={{ color: 'var(--text)', margin: 0 }}>Access Denied</h2>
        <p style={{ color: 'var(--text-secondary)', maxWidth: 360 }}>
          Your role <strong style={{ color: '#3B6082' }}>{user?.role?.replace('_', ' ')}</strong> does not have permission to access this page.
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Contact your administrator if you believe this is an error.</p>
        <a href="/dashboard" style={{ marginTop: 8, color: '#3B6082', textDecoration: 'none', fontWeight: 600 }}>Return to Dashboard</a>
      </div>
    );
  }
  return children;
}

// ── Public root: Landing page for guests, Dashboard for auth users ───────────────
function RootRoute() {
  const { user, authChecked, loading } = useAuth();
  if (!authChecked || loading) return <AuthLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

// ── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />

          {/* Protected app shell */}
          <Route path="/" element={<BlockUnauthenticated><Layout /></BlockUnauthenticated>}>
            <Route path="dashboard" element={<RequirePage pageKey="dashboard"><Dashboard /></RequirePage>} />
            <Route path="work-orders" element={<RequirePage pageKey="workOrders"><WorkOrders /></RequirePage>} />
            <Route path="work-orders/:id" element={<RequirePage pageKey="workOrders"><WorkOrderDetail /></RequirePage>} />
            <Route path="maintenance-plan" element={<RequirePage pageKey="maintenancePlan"><MaintenancePlan /></RequirePage>} />
            <Route path="work-plans" element={<RequirePage pageKey="workPlans"><WorkPlans /></RequirePage>} />
            <Route path="inventory" element={<RequirePage pageKey="inventory"><Inventory /></RequirePage>} />
            <Route path="purchasing" element={<RequirePage pageKey="purchasing"><Purchasing /></RequirePage>} />
            <Route path="reports" element={<RequirePage pageKey="reports"><Reports /></RequirePage>} />
            <Route path="misc" element={<RequirePage pageKey="miscellaneous"><Miscellaneous /></RequirePage>} />
          </Route>

          {/* Catch-all → root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
